import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { getCurrentUser } from "../../lib/auth.js";
import { useApiData } from "../../lib/api.js";
import { useNahledTrenera } from "../../lib/nahledTrenera.js";
// SystemAlertBanner je v Layout.jsx (parent route), tady ho NEpotřebujeme — jinak se renderuje 2×.

// Layout pro trenérskou sekci. Mobile-first.
//
// 4.9.2026: výběr trenéra pro ownera a jeho zvoneček se přestěhovaly do horní
// lišty aplikace (components/VyberTrenera.jsx, layout/TopBar.jsx). Tady zůstal
// jen přepínač zástupů, když trenér zaskakuje na cizím boardu.

export default function TrenerLayout() {
  const user = getCurrentUser();
  // Reálná role (i v náhledu "Trenér" zůstává owner ownerem) — jinak by se skryl výběr trenéra.
  const isOwner = (user?._realRole || user?.role) === "owner";
  // Výběr ownera drží sdílený modul (localStorage + událost na window), aby ho
  // viděla i horní lišta, která je v jiné části stromu.
  const [nahled] = useNahledTrenera();
  const viewAsTrener = isOwner ? nahled : (user?.linkedTrener || "");

  // Feedback #66 (13.8.2026): zástupy — trenér smí přepnout na board kolegy
  // (nastavuje owner v trener_zastupy, např. Kuba řeší úkoly Petra).
  const { data: zastupyData } = useApiData(!isOwner ? "/api/trener-zastupy" : null);
  const mujBoard = user?.linkedTrener || user?.name || "";
  const zastupy = useMemo(() => (zastupyData?.moje || []).filter((p) => p && p !== mujBoard), [zastupyData, mujBoard]);
  const [zastupVolba, setZastupVolba] = useState("");
  const activeTrener = isOwner
    ? (viewAsTrener || "")
    : ((zastupVolba && zastupy.includes(zastupVolba)) ? zastupVolba : mujBoard);

  // Pro trenéra (non-owner) načteme jen vlastní record /api/my-trainer (foto + jmeno).
  // Owner v náhledu: stejný endpoint s ?jmeno= — ať náhled respektuje status
  // Ukončeno/Bývalý úplně stejně, jako ho uvidí sám trenér (17.7.2026).
  const { data: myTrainerData } = useApiData(
    !isOwner ? "/api/my-trainer"
    : (viewAsTrener ? `/api/my-trainer?jmeno=${encodeURIComponent(viewAsTrener)}` : null)
  );
  // Status "Ukončeno" (16.7.2026): trenér v ukončené spolupráci vidí UZ JEN
  // Fakturaci (žádný dashboard, klienti, úkoly...). Nastavuje se v HR → Lidé
  // (trainers.status), klienty postupně přebírají jiní trenéři.
  // "Ukončeno" i "Bývalý" (active=false): obojí omezí trenéra na Fakturaci.
  const trenerUkoncen = /ukon/i.test(String(myTrainerData?.trainer?.status || "")) || myTrainerData?.trainer?.active === false;
  const location = useLocation();
  const navigateU = useNavigate();
  useEffect(() => {
    if (trenerUkoncen && !location.pathname.startsWith("/trener/fakturace")) {
      navigateU("/trener/fakturace", { replace: true });
    }
  }, [trenerUkoncen, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* Přepínač zástupů. Ukazuje se jen trenérovi, který zaskakuje na cizím boardu. */}
      {!isOwner && zastupy.length > 0 && (
        <div className="glass min-w-0 p-3 sm:p-4">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {[mujBoard, ...zastupy].map((p) => (
              <button key={p} type="button"
                onClick={() => setZastupVolba(p === mujBoard ? "" : p)}
                className={`demo-sensitive inline-flex min-h-[40px] items-center rounded-full border px-3 text-xs font-bold transition-colors ${activeTrener === p ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-700"}`}
                title={p === mujBoard ? "Můj board" : `Zástup: board ${p}`}>
                {p === mujBoard ? "Moje" : p.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Obsah konkrétní podstránky */}
      {trenerUkoncen && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Spolupráce je ukončená, k dispozici zůstává jen sekce Fakturace.
        </div>
      )}
      <Outlet context={{ activeTrener, isOwner, viewAsTrener }} />

    </div>
  );
}
